import { useApolloClient, useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { PostHogVariant } from '@eluve/api-contract';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Checkbox,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

import {
  createExperimentVariantMutation,
  deleteExperimentVariantMutation,
  getExperimentByIdQuery,
  updateExperimentVariantMutation,
} from './operations';
import {
  CURRENT_CONFIG_VERSION,
  OverrideKey,
  VariantConfig,
  VersionedVariantConfig,
} from './overrides';

const PAYLOAD_KEY_MAP: Record<string, OverrideKey> = {
  stt_provider: 'STT_PROVIDER',
  gemini_prompt: 'GEMINI_PROMPT',
  gemini_model_arg: 'GEMINI_MODEL_ARG',
  prompt_template_id: 'PROMPT_TEMPLATE_ID',
  prompt_template_variant_id: 'PROMPT_TEMPLATE_VARIANT_ID',
  output_template_id: 'OUTPUT_TEMPLATE_ID',
  output_template_variant_id: 'OUTPUT_TEMPLATE_VARIANT_ID',
  prompt_model_arg: 'PROMPT_MODEL_ARG',
};

const mapPayloadToConfig = (
  payload: Record<string, unknown>,
): VariantConfig => {
  const config: VariantConfig = {};
  for (const [key, value] of Object.entries(payload)) {
    const mappedKey = PAYLOAD_KEY_MAP[key.toLowerCase()];
    if (mappedKey && value !== undefined && value !== null) {
      (config as Record<string, unknown>)[mappedKey] = String(value);
    }
  }
  return config;
};

type ExistingVariant = {
  id: string;
  posthogVariantKey: string;
  variantConfig: Record<string, unknown>;
};

type VariantToUpdate = {
  id: string;
  posthogVariantKey: string;
  newConfig: VariantConfig;
};

interface SyncFromPostHogDialogProps {
  experimentId: string;
  posthogFlagKey: string;
  existingVariants: ExistingVariant[];
  isActivated: boolean;
}

export const SyncFromPostHogDialog: React.FC<SyncFromPostHogDialogProps> = ({
  experimentId,
  posthogFlagKey,
  existingVariants,
  isActivated,
}) => {
  const toast = useToast();
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [variantsToAdd, setVariantsToAdd] = useState<PostHogVariant[]>([]);
  const [variantsToRemove, setVariantsToRemove] = useState<ExistingVariant[]>(
    [],
  );
  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set());
  const [selectedToRemove, setSelectedToRemove] = useState<Set<string>>(
    new Set(),
  );
  const [variantsToUpdate, setVariantsToUpdate] = useState<VariantToUpdate[]>(
    [],
  );
  const [selectedToUpdate, setSelectedToUpdate] = useState<Set<string>>(
    new Set(),
  );
  const [error, setError] = useState<string | null>(null);

  const [createVariant] = useMutation(createExperimentVariantMutation);

  const [deleteVariant] = useMutation(deleteExperimentVariantMutation);

  const [updateVariant] = useMutation(updateExperimentVariantMutation);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setVariantsToAdd([]);
    setVariantsToRemove([]);
    setVariantsToUpdate([]);
    setSelectedToAdd(new Set());
    setSelectedToRemove(new Set());
    setSelectedToUpdate(new Set());

    try {
      const response = await apiClient.experiments.syncFromPostHog({
        body: { posthogFlagKey },
      });

      if (response.status === 200) {
        const posthogKeys = new Set(response.body.variants.map((v) => v.key));
        const existingKeys = new Set(
          existingVariants.map((v) => v.posthogVariantKey),
        );

        const toAdd = response.body.variants.filter(
          (v) => !existingKeys.has(v.key),
        );
        const toRemove = existingVariants.filter(
          (v) => !posthogKeys.has(v.posthogVariantKey),
        );

        // Find existing variants whose PostHog payload has config we should apply
        const existingByKey = new Map(
          existingVariants.map((v) => [v.posthogVariantKey, v]),
        );
        const toUpdate: VariantToUpdate[] = [];
        for (const phVariant of response.body.variants) {
          const existing = existingByKey.get(phVariant.key);
          if (!existing || !phVariant.payload) continue;

          const newConfig = mapPayloadToConfig(phVariant.payload);
          if (Object.keys(newConfig).length === 0) continue;

          // Compare against existing config
          const existingConfig =
            existing.variantConfig &&
            typeof existing.variantConfig === 'object' &&
            'config' in existing.variantConfig
              ? (existing.variantConfig as unknown as VersionedVariantConfig)
                  .config
              : existing.variantConfig;

          const hasChanges = Object.entries(newConfig).some(
            ([k, v]) => (existingConfig as Record<string, unknown>)?.[k] !== v,
          );

          if (hasChanges) {
            toUpdate.push({
              id: existing.id,
              posthogVariantKey: existing.posthogVariantKey,
              newConfig,
            });
          }
        }

        setVariantsToAdd(toAdd);
        setVariantsToRemove(toRemove);
        setVariantsToUpdate(toUpdate);
        setSelectedToAdd(new Set(toAdd.map((v) => v.key)));
        setSelectedToRemove(new Set(toRemove.map((v) => v.id)));
        setSelectedToUpdate(new Set(toUpdate.map((v) => v.id)));

        if (
          toAdd.length === 0 &&
          toRemove.length === 0 &&
          toUpdate.length === 0
        ) {
          setError('Variants are already in sync with PostHog');
        }
      } else if (response.status === 404 || response.status === 500) {
        setError(response.body.error);
      }
    } catch {
      setError('Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  const toggleAddVariant = (key: string) => {
    const newSelected = new Set(selectedToAdd);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedToAdd(newSelected);
  };

  const toggleRemoveVariant = (id: string) => {
    const newSelected = new Set(selectedToRemove);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedToRemove(newSelected);
  };

  const toggleUpdateVariant = (id: string) => {
    const newSelected = new Set(selectedToUpdate);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedToUpdate(newSelected);
  };

  const handleSync = async () => {
    if (
      selectedToAdd.size === 0 &&
      selectedToRemove.size === 0 &&
      selectedToUpdate.size === 0
    ) {
      toast.error('Please select at least one change to sync');
      return;
    }

    setSyncing(true);
    let addedCount = 0;
    let removedCount = 0;
    let updatedCount = 0;
    let failCount = 0;

    for (const variant of variantsToAdd) {
      if (!selectedToAdd.has(variant.key)) continue;

      try {
        const config = variant.payload
          ? mapPayloadToConfig(variant.payload)
          : {};
        const versionedConfig: VersionedVariantConfig = {
          version: CURRENT_CONFIG_VERSION,
          publishedAt: new Date().toISOString(),
          config,
        };
        await createVariant({
          variables: {
            experimentId,
            name: variant.key,
            posthogVariantKey: variant.key,
            variantConfig: versionedConfig,
          },
        });
        addedCount++;
      } catch {
        failCount++;
      }
    }

    for (const variant of variantsToRemove) {
      if (!selectedToRemove.has(variant.id)) continue;

      try {
        await deleteVariant({
          variables: { variantId: variant.id },
        });
        removedCount++;
      } catch {
        failCount++;
      }
    }

    for (const variant of variantsToUpdate) {
      if (!selectedToUpdate.has(variant.id)) continue;

      try {
        const versionedConfig: VersionedVariantConfig = {
          version: CURRENT_CONFIG_VERSION,
          publishedAt: new Date().toISOString(),
          config: variant.newConfig,
        };
        await updateVariant({
          variables: {
            variantId: variant.id,
            variantConfig: versionedConfig,
          },
        });
        updatedCount++;
      } catch {
        failCount++;
      }
    }

    // Single refetch after all mutations complete to ensure cache is updated
    await apolloClient.refetchQueries({
      include: [getExperimentByIdQuery],
    });

    setSyncing(false);
    setOpen(false);

    const messages: string[] = [];
    if (addedCount > 0) messages.push(`added ${addedCount}`);
    if (updatedCount > 0) messages.push(`updated ${updatedCount}`);
    if (removedCount > 0) messages.push(`removed ${removedCount}`);

    if (failCount === 0) {
      toast.success(`Synced: ${messages.join(', ')}`);
    } else {
      toast.error(`${messages.join(', ')}, ${failCount} failed`);
    }
  };

  const totalSelected =
    selectedToAdd.size + selectedToRemove.size + selectedToUpdate.size;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          onClick={handleOpen}
          disabled={isActivated || !posthogFlagKey}
        >
          Sync from PostHog
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sync Variants from PostHog</AlertDialogTitle>
          <AlertDialogDescription>
            Fetching variants for flag: <strong>{posthogFlagKey}</strong>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Stack gap={3} className="py-4">
          {loading && (
            <Text size="md" color="tertiary">
              Loading variants from PostHog...
            </Text>
          )}

          {error && (
            <Text size="md" color="tertiary">
              {error}
            </Text>
          )}

          {!loading &&
            !error &&
            variantsToAdd.length === 0 &&
            variantsToRemove.length === 0 &&
            variantsToUpdate.length === 0 && (
              <Text size="md" color="tertiary">
                No changes needed
              </Text>
            )}

          {!loading && variantsToAdd.length > 0 && (
            <Stack gap={2}>
              <Text size="xs" color="secondary">
                Variants to add:
              </Text>
              {variantsToAdd.map((variant) => (
                <Stack
                  key={variant.key}
                  gap={2}
                  align="center"
                  className="rounded-sm border border-border-primary bg-bg-positive-subtle p-2"
                  direction="horizontal"
                >
                  <Checkbox
                    checked={selectedToAdd.has(variant.key)}
                    onCheckedChange={() => toggleAddVariant(variant.key)}
                  />
                  <Stack>
                    <Text size="md" inline>
                      {variant.key}
                    </Text>
                    <Text inline size="xs" color="tertiary">
                      Key: {variant.key} ({variant.rollout_percentage}%)
                    </Text>
                    {variant.payload &&
                      Object.keys(variant.payload).length > 0 && (
                        <Text inline size="xs" color="secondary">
                          Config:{' '}
                          {Object.entries(mapPayloadToConfig(variant.payload))
                            .map(([k, v]) => `${k}=${v}`)
                            .join(', ')}
                        </Text>
                      )}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          {!loading && variantsToUpdate.length > 0 && (
            <Stack gap={2}>
              <Text size="xs" color="secondary">
                Variants to update config:
              </Text>
              {variantsToUpdate.map((variant) => (
                <Stack
                  key={variant.id}
                  gap={2}
                  align="center"
                  className="rounded-sm border border-border-primary bg-bg-warning-subtle p-2"
                  direction="horizontal"
                >
                  <Checkbox
                    checked={selectedToUpdate.has(variant.id)}
                    onCheckedChange={() => toggleUpdateVariant(variant.id)}
                  />
                  <Stack>
                    <Text size="md" inline>
                      {variant.posthogVariantKey}
                    </Text>
                    <Text inline size="xs" color="secondary">
                      Config:{' '}
                      {Object.entries(variant.newConfig)
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ')}
                    </Text>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          {!loading && variantsToRemove.length > 0 && (
            <Stack gap={2}>
              <Text size="xs" color="secondary">
                Variants to remove (not in PostHog):
              </Text>
              {variantsToRemove.map((variant) => (
                <Stack
                  key={variant.id}
                  gap={2}
                  align="center"
                  className="rounded-sm border border-border-primary bg-bg-negative-subtle p-2"
                  direction="horizontal"
                >
                  <Checkbox
                    checked={selectedToRemove.has(variant.id)}
                    onCheckedChange={() => toggleRemoveVariant(variant.id)}
                  />
                  <Stack>
                    <Text size="md" inline>
                      {variant.posthogVariantKey}
                    </Text>
                    <Text inline size="xs" color="tertiary">
                      Will be deleted
                    </Text>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSync}
            disabled={loading || syncing || totalSelected === 0}
          >
            {syncing
              ? 'Syncing...'
              : `Sync ${totalSelected} Change${totalSelected !== 1 ? 's' : ''}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
